import type { components } from './generated/api.models';

type ActualTrialConfiguration = Pick<components['schemas']['TrialEventAdminDetailDto'],
  'trialType' | 'offersStockSheep' | 'offersStockCattle' | 'offersStockDucks' |
  'offersStockMixed' | 'allowsFeo' | 'actualTrials' | 'configurationVersion'>;

type RegistrationActualTrialConfiguration = Pick<components['schemas']['TrialEventRegistrationConfigDto'],
  keyof ActualTrialConfiguration>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasCompetitiveClasses(value: unknown): boolean {
  return isRecord(value) &&
    ['std', 'opn', 'adv', 'ftdOpn', 'ftdAdv', 'nov', 'wrkJrHndlr', 'postAdv', 'rtd']
      .every(field => typeof value[field] === 'boolean');
}

/** Old or incomplete metadata cannot imply any type, offer, slot, count or rate. */
export function hasActualTrialConfiguration(value: unknown): value is ActualTrialConfiguration {
  if (!isRecord(value) || typeof value.trialType !== 'string' ||
      !['Arena', 'Farm', 'Ranch', 'PostAdvanced'].includes(value.trialType) ||
      typeof value.configurationVersion !== 'string' || value.configurationVersion.length === 0 ||
      !['offersStockSheep', 'offersStockCattle', 'offersStockDucks', 'offersStockMixed', 'allowsFeo']
        .every(field => typeof value[field] === 'boolean') ||
      !Array.isArray(value.actualTrials) || value.actualTrials.length === 0) return false;
  return value.actualTrials.every((trial: unknown) =>
    isRecord(trial) && typeof trial.actualTrialId === 'string' && trial.actualTrialId.length > 0 &&
    typeof trial.date === 'string' && (trial.trialNumberOnDate === 1 || trial.trialNumberOnDate === 2) &&
    Array.isArray(trial.stocks) && trial.stocks.every((stock: unknown) =>
      isRecord(stock) && typeof stock.trialStockOfferId === 'string' && stock.trialStockOfferId.length > 0 &&
      typeof stock.stock === 'string' && ['Sheep', 'Cattle', 'Ducks', 'Mixed'].includes(stock.stock) &&
      typeof stock.headCount === 'number' && Number.isInteger(stock.headCount) && stock.headCount >= 0 &&
      typeof stock.preEntryFee === 'number' && Number.isFinite(stock.preEntryFee) && stock.preEntryFee >= 0 &&
      typeof stock.dayOfFee === 'number' && Number.isFinite(stock.dayOfFee) && stock.dayOfFee >= 0));
}

/** Registration additionally requires stock-specific competitive and FEO availability. */
export function hasRegistrationActualTrialConfiguration(value: unknown): value is RegistrationActualTrialConfiguration {
  return hasActualTrialConfiguration(value) &&
    value.actualTrials.every(trial => trial.stocks.every(stock =>
      'allowsFeo' in stock && typeof stock.allowsFeo === 'boolean' &&
      'classes' in stock && hasCompetitiveClasses(stock.classes)));
}
